class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    this.wsURL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    const token = localStorage.getItem('sarvik_token');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('sarvik_token');
        localStorage.removeItem('sarvik_user');
        window.location.href = '/login';
        throw new Error('Authentication required');
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Auth endpoints
  async verifyToken(token) {
    return this.request('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async getUserProfile() {
    return this.request('/auth/profile');
  }

  async deleteAccount() {
    const token = localStorage.getItem('sarvik_token');
    
    try {
      const response = await this.request(`/auth/account?token=${token}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      // Enhanced error message for better debugging
      console.error('Delete account API error:', error);
      throw new Error(error.message || 'Failed to delete account');
    }
  }

  // Voice endpoints
  async processAudio(audioBlob) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    
    return this.request('/voice/process', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set content-type for FormData
    });
  }

  async updateVoiceSettings(settings) {
    return this.request('/voice/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Conversation endpoints
  async getConversations(limit = 50, offset = 0) {
    return this.request(`/conversations?limit=${limit}&offset=${offset}`);
  }

  async getConversation(conversationId) {
    return this.request(`/conversations/${conversationId}`);
  }

  // Phase 2B - Conversation History
  async getConversationHistory(limit = 50, offset = 0, mode = null) {
    const token = localStorage.getItem('sarvik_token');
    const modeParam = mode ? `&mode=${mode}` : '';
    return this.request(`/conversations/history?token=${token}&limit=${limit}&offset=${offset}${modeParam}`, {
      method: 'GET',
    });
  }

  async clearConversationHistory() {
    const token = localStorage.getItem('sarvik_token');
    return this.request(`/conversations/clear?token=${token}&confirm=true`, {
      method: 'DELETE',
    });
  }

  async deleteConversation(conversationId) {
    const token = localStorage.getItem('sarvik_token');
    return this.request(`/conversations/${conversationId}?token=${token}`, {
      method: 'DELETE',
    });
  }

  async deleteConversationsByDate(date) {
    const token = localStorage.getItem('sarvik_token');
    return this.request(`/conversations/date/${date}?token=${token}`, {
      method: 'DELETE',
    });
  }

  // Memory endpoints
  async getMemories(type = null) {
    const typeParam = type ? `?type=${type}` : '';
    return this.request(`/memory${typeParam}`);
  }

  async createMemory(memory) {
    return this.request('/memory', {
      method: 'POST',
      body: JSON.stringify(memory),
    });
  }

  async updateMemory(memoryId, updates) {
    return this.request(`/memory/${memoryId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteMemory(memoryId) {
    return this.request(`/memory/${memoryId}`, {
      method: 'DELETE',
    });
  }

  // Reminders endpoints
  async getReminders(status = 'active') {
    return this.request(`/reminders?status=${status}`);
  }

  async createReminder(reminder) {
    return this.request('/reminders', {
      method: 'POST',
      body: JSON.stringify(reminder),
    });
  }

  async updateReminder(reminderId, updates) {
    return this.request(`/reminders/${reminderId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteReminder(reminderId) {
    return this.request(`/reminders/${reminderId}`, {
      method: 'DELETE',
    });
  }

  // Settings endpoints
  async getSettings() {
    return this.request('/settings');
  }

  async updateSettings(settings) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Timezone endpoints (Phase 2A - Context Management)
  async getUserTimezone() {
    const token = localStorage.getItem('sarvik_token');
    return this.request(`/settings/timezone?token=${token}`, {
      method: 'GET',
    });
  }

  async updateUserTimezone(timezone) {
    const token = localStorage.getItem('sarvik_token');
    return this.request('/settings/timezone', {
      method: 'PUT',
      body: JSON.stringify({ token, timezone }),
    });
  }

  async startVoiceEnrollment(token) {
    return this.request('/voice/enrollment/start', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async recordEnrollmentPhrase(sessionId, phraseIndex, audioBlob) {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('phrase_index', phraseIndex);
    formData.append('audio', audioBlob, 'recording.webm');
    
    return this.request('/voice/enrollment/record-phrase', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set content-type for FormData
    });
  }

  async completeEnrollment(sessionId) {
    return this.request('/voice/enrollment/complete', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  async verifyVoice(audioBlob, token) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'verification.webm');
    formData.append('token', token);
    
    return this.request('/voice/verify', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  }

  async getVoiceProfile(token) {
    return this.request(`/voice/profile?token=${token}`);
  }

  // LLM Provider Settings endpoints
  async getLLMSettings(token) {
    return this.request(`/llm-settings?token=${token}`, {
      method: 'GET',
    });
  }

  async updateLLMProvider(token, provider) {
    return this.request(`/llm-settings/provider?token=${token}`, {
      method: 'PUT',
      body: JSON.stringify({ provider }),
    });
  }

  async saveGroqApiKey(token, apiKey, model = 'llama-3.3-70b-versatile') {
    return this.request(`/llm-settings/groq-key?token=${token}`, {
      method: 'POST',
      body: JSON.stringify({ api_key: apiKey, model }),
    });
  }

  async updateGroqModel(token, model) {
    return this.request(`/llm-settings/groq-model?token=${token}`, {
      method: 'PUT',
      body: JSON.stringify({ model }),
    });
  }

  async testGroqConnection(token) {
    return this.request(`/llm-settings/test-connection?token=${token}`, {
      method: 'POST',
    });
  }

  async deleteGroqApiKey(token) {
    return this.request(`/llm-settings/groq-key?token=${token}`, {
      method: 'DELETE',
    });
  }

  // WebSocket connection
  createWebSocketConnection(userId) {
    const token = localStorage.getItem('auth_token');
    const wsUrl = `${this.wsURL}/${userId}?token=${token}`;
    return new WebSocket(wsUrl);
  }
}

export const apiService = new ApiService();
export default apiService;
